import React, { useState, useEffect } from 'react';
import { CheckCircle, FileSymlink, Upload, Edit, RotateCcw, MessageSquare, X, FileText, Type } from 'lucide-react';
import { formatDate } from '../../utils/helpers';
import { API_BASE_URL } from '../../utils/constants';

/**
 * Component for submitting assignments
 * Enhanced to support foundational, preparatory, and middle stages
 * Now supports both file uploads and text-based answers with proper resubmission handling
 *
 * @param {Object} props
 * @param {Object} props.assignment - Assignment data with questions and settings
 * @param {Object} props.submission - Current submission data if exists
 * @param {File} props.uploadFile - The file selected for upload
 * @param {Function} props.handleFileChange - Function to handle file selection
 * @param {Function} props.handleClearUploadFile - Function to clear upload file
 * @param {Function} props.handleSubmit - Function to handle unified submission (file or answers)
 * @param {boolean} props.isPreparatory - Whether this is for preparatory stage
 * @param {boolean} props.isMiddle - Whether this is for middle stage
 */

const FILE_BASE_URL = API_BASE_URL;

const SubmissionForm = ({
    assignment,
    submission,
    uploadFile,
    handleFileChange,
    handleClearUploadFile,
    handleSubmit,
    isPreparatory = false,
    isMiddle = false
}) => {
    // State for text-based answers
    const [answers, setAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResubmitting, setIsResubmitting] = useState(false);

    // State for submission type choice (for new submissions and resubmissions)
    const [submissionType, setSubmissionType] = useState(null); // 'file' or 'text'

    // Initialize answers from existing submission
    useEffect(() => {
        if (submission?.answers) {
            if (Array.isArray(submission.answers)) {
                const answersObj = {};
                submission.answers.forEach((answer, index) => {
                    answersObj[index] = answer;
                });
                setAnswers(answersObj);
            }
        }
    }, [submission]);

    // Determine submission state
    const hasAssessmentQuestions = assignment?.assessment_questions && assignment.assessment_questions.length > 0;
    const allowResubmit = submission?.allow_resubmit === true;
    const hasTextAnswers = submission?.answers && Array.isArray(submission.answers) && submission.answers.length > 0;
    const hasFileSubmission = submission?.file_path;
    const hasAnySubmission = hasTextAnswers || hasFileSubmission;

    // Debug logs
    console.log('SubmissionForm Debug:', {
        hasAssessmentQuestions,
        allowResubmit,
        hasTextAnswers,
        hasFileSubmission,
        hasAnySubmission,
        submissionType,
        isResubmitting,
        submission
    });

    // Get stage-specific content
    const getStageName = () => {
        if (isMiddle) return "Middle Stage";
        if (isPreparatory) return "Preparatory Stage";
        return "Foundational Stage";
    };

    // Get stage-specific instructions
    const getStageInstructions = () => {
        if (hasAnySubmission && !isResubmitting) {
            return `Your ${hasTextAnswers ? 'answers' : 'file'} has been submitted successfully.`;
        }

        if (isResubmitting) {
            return `Update your ${submissionType === 'text' ? 'answers' : 'file'} below.`;
        }

        if (submissionType === 'text') {
            return isMiddle || isPreparatory
                ? "Answer the questions below. After submitting, complete the self-assessment and peer feedback forms."
                : "Answer the questions below";
        } else if (submissionType === 'file') {
            return isMiddle || isPreparatory
                ? "Upload your completed work. After submitting, complete the self-assessment and peer feedback forms."
                : "Upload your completed work";
        } else {
            return "Choose how you want to submit your assignment.";
        }
    };

    // Handle submission type change - clear conflicting data
    const handleSubmissionTypeChange = (newType) => {
        console.log('Changing submission type to:', newType);

        if (newType === 'text') {
            // Switching to text - clear any file data
            if (uploadFile && handleClearUploadFile) {
                console.log('Clearing file data for text submission');
                handleClearUploadFile();
            }
        } else if (newType === 'file') {
            // Switching to file - clear any text answers (for fresh start)
            if (Object.keys(answers).length > 0 && !submission?.answers) {
                console.log('Clearing text answers for file submission');
                setAnswers({});
            }
        }

        setSubmissionType(newType);
    };

    // Handle answer text change
    const handleAnswerChange = (questionIndex, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionIndex]: value
        }));
    };

    // Handle text answers submission
    const handleTextSubmission = async (e) => {
        e.preventDefault();

        // Validate that all questions are answered
        if (hasAssessmentQuestions) {
            const unansweredQuestions = assignment.assessment_questions.filter(
                (_, index) => !answers[index] || answers[index].trim() === ''
            );

            if (unansweredQuestions.length > 0) {
                alert("Please answer all questions before submitting.");
                return;
            }
        }

        // Convert answers object to array format expected by backend
        const answersArray = hasAssessmentQuestions
            ? assignment.assessment_questions.map((_, index) => {
                const answer = answers[index];
                return answer && answer.trim() ? answer.trim() : '';
            }).filter(answer => answer !== '') // Remove empty answers
            : Object.values(answers).filter(answer => answer && answer.trim() !== '').map(answer => answer.trim());

        if (answersArray.length === 0) {
            alert("Please provide at least one answer.");
            return;
        }

        console.log('Submitting answers array:', answersArray); // Debug log

        setIsSubmitting(true);
        try {
            // IMPORTANT: Clear any file upload data before text submission
            if (uploadFile && handleClearUploadFile) {
                console.warn('Clearing upload file before text submission');
                handleClearUploadFile();
            }

            // Use unified submit handler with text flag
            await handleSubmit(answersArray, true); // true indicates text submission
            setIsResubmitting(false); // Close resubmit mode on success
            setSubmissionType(null); // Reset submission type
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle file submission wrapper
    const handleFileSubmission = async (e) => {
        e.preventDefault();

        if (!uploadFile) {
            alert("Please select a file to upload.");
            return;
        }

        setIsSubmitting(true);
        try {
            // IMPORTANT: Clear any text answers before file submission
            if (Object.keys(answers).length > 0) {
                console.warn('Clearing text answers before file submission');
                setAnswers({}); // Clear all text answers
            }

            // Use unified submit handler with file
            await handleSubmit(uploadFile, false); // false indicates file submission
            setIsResubmitting(false); // Close resubmit mode on success
            setSubmissionType(null); // Reset submission type
        } catch (error) {
            // Error handling is done in parent component
        } finally {
            setIsSubmitting(false);
        }
    };

    // Start resubmission process
    const startResubmission = () => {
        setIsResubmitting(true);
        // Auto-select submission type based on existing submission
        if (hasTextAnswers) {
            setSubmissionType('text');
        } else if (hasFileSubmission) {
            setSubmissionType('file');
        }
    };

    // Cancel resubmission
    const cancelResubmission = () => {
        setIsResubmitting(false);
        setSubmissionType(null);
        // Reset any changes
        if (submission?.answers) {
            if (Array.isArray(submission.answers)) {
                const answersObj = {};
                submission.answers.forEach((answer, index) => {
                    answersObj[index] = answer;
                });
                setAnswers(answersObj);
            }
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex justify-between items-start">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                <Upload className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">
                                Submit Assignment
                            </h2>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            {getStageInstructions()}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {(isMiddle || isPreparatory) && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                    {getStageName()}
                                </span>
                            )}
                            {hasAnySubmission && allowResubmit && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    Re-submission Allowed
                                </span>
                            )}
                            {hasAnySubmission && !allowResubmit && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                    Re-submission Not Allowed
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Cancel resubmission button */}
                    {isResubmitting && (
                        <button
                            onClick={cancelResubmission}
                            className="ml-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                            title="Cancel resubmission"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>
            </div>

            <div className="p-6">
                {/* Case 1: No submission yet - Show submission type choice or form */}
                {!hasAnySubmission && (
                    <>
                        {submissionType === null ? (
                            /* Submission Type Selection */
                            <div className="space-y-6">
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Choose Submission Method</h3>
                                    <p className="text-sm text-gray-600 mb-6">Select how you want to submit your assignment</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Text Answers Option */}
                                    {hasAssessmentQuestions && (
                                        <button
                                            onClick={() => handleSubmissionTypeChange('text')}
                                            className="p-6 border-2 border-gray-200 rounded-xl hover:border-[#D67C2C] hover:bg-orange-50 transition-all group shadow-sm hover:shadow-md"
                                        >
                                            <div className="text-center">
                                                <Type className="w-12 h-12 mx-auto mb-3 text-gray-400 group-hover:text-[#D67C2C]" />
                                                <h4 className="text-lg font-semibold text-gray-900 mb-2">Text Answers</h4>
                                                <p className="text-sm text-gray-600">
                                                    Answer {assignment.assessment_questions.length} questions directly
                                                </p>
                                            </div>
                                        </button>
                                    )}

                                    {/* File Upload Option */}
                                    <button
                                        onClick={() => handleSubmissionTypeChange('file')}
                                        className="p-6 border-2 border-gray-200 rounded-xl hover:border-[#D67C2C] hover:bg-orange-50 transition-all group shadow-sm hover:shadow-md"
                                    >
                                        <div className="text-center">
                                            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400 group-hover:text-[#D67C2C]" />
                                            <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload File</h4>
                                            <p className="text-sm text-gray-600">
                                                Upload a document, image, or other file
                                            </p>
                                        </div>
                                    </button>
                                </div>

                                {/* If no assessment questions, default to file upload */}
                                {!hasAssessmentQuestions && (
                                    <div className="text-center">
                                        <p className="text-sm text-gray-500 mb-4">No assessment questions available for this assignment</p>
                                        <button
                                            onClick={() => handleSubmissionTypeChange('file')}
                                            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C] transition-colors"
                                        >
                                            <Upload size={16} className="mr-2" />
                                            Upload File
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : submissionType === 'text' ? (
                            /* Text Submission Form */
                            <form onSubmit={handleTextSubmission} className="space-y-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Text Answers</h3>
                                    <button
                                        type="button"
                                        onClick={() => handleSubmissionTypeChange(null)}
                                        className="text-sm text-gray-500 hover:text-gray-700"
                                    >
                                        ← Change method
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {assignment.assessment_questions.map((question, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-900 mb-2">
                                                <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-100 text-orange-600 rounded-full text-xs font-semibold mr-2">
                                                    {index + 1}
                                                </span>
                                                {question}
                                            </label>
                                            <textarea
                                                value={answers[index] || ''}
                                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-vertical min-h-[100px]"
                                                placeholder="Type your answer here..."
                                                required
                                            />
                                        </div>
                                    ))}
                                </div>

                                {(isMiddle || isPreparatory) && (
                                    <div className="bg-blue-50 px-4 py-3 rounded-md text-sm text-blue-700">
                                        <p className="font-medium mb-1">Important Note:</p>
                                        <p>For {isMiddle ? 'Middle' : 'Preparatory'} Stage activities, you'll need to complete all components:</p>
                                        <ul className="list-disc list-inside mt-1">
                                            <li>Answer submission (this step)</li>
                                            <li>Self-assessment</li>
                                            <li>Peer assessment</li>
                                        </ul>
                                    </div>
                                )}

                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <MessageSquare size={16} className="mr-2" />
                                                Submit Answers
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            /* File Submission Form */
                            <form onSubmit={handleFileSubmission} className="space-y-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Upload File</h3>
                                    {hasAssessmentQuestions && (
                                        <button
                                            type="button"
                                            onClick={() => handleSubmissionTypeChange(null)}
                                            className="text-sm text-gray-500 hover:text-gray-700"
                                        >
                                            ← Change method
                                        </button>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Upload your assignment file</label>
                                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                                        <div className="space-y-1 text-center">
                                            <Upload className="mx-auto h-12 w-12 text-gray-400" />
                                            <div className="flex text-sm text-gray-600">
                                                <label
                                                    htmlFor="file-upload"
                                                    className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none"
                                                >
                                                    <span>Upload a file</span>
                                                    <input
                                                        id="file-upload"
                                                        name="file-upload"
                                                        type="file"
                                                        className="sr-only"
                                                        onChange={handleFileChange}
                                                    />
                                                </label>
                                                <p className="pl-1">or drag and drop</p>
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                PDF, DOCX, XLSX, ZIP, PNG, JPG up to 10MB
                                            </p>
                                        </div>
                                    </div>
                                    {uploadFile && (
                                        <p className="mt-2 text-sm text-gray-600">
                                            Selected file: <span className="font-medium">{uploadFile.name}</span>
                                        </p>
                                    )}
                                </div>

                                {(isMiddle || isPreparatory) && (
                                    <div className="bg-blue-50 px-4 py-3 rounded-md text-sm text-blue-700">
                                        <p className="font-medium mb-1">Important Note:</p>
                                        <p>For {isMiddle ? 'Middle' : 'Preparatory'} Stage activities, you'll need to complete all components:</p>
                                        <ul className="list-disc list-inside mt-1">
                                            <li>File submission (this step)</li>
                                            <li>Self-assessment</li>
                                            <li>Peer assessment</li>
                                        </ul>
                                    </div>
                                )}

                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={16} className="mr-2" />
                                                Submit File
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </>
                )}

                {/* Case 2: Has submission - Show submission status */}
                {hasAnySubmission && !isResubmitting && (
                    <div className="space-y-4">
                        {/* Submission Success Status */}
                        <div className="rounded-md bg-green-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <CheckCircle className="h-5 w-5 text-green-400" />
                                </div>
                                <div className="ml-3 flex-1">
                                    <h3 className="text-sm font-medium text-green-800">
                                        {hasTextAnswers ? 'Text Answers Submitted' : 'File Submitted'}
                                    </h3>
                                    <div className="mt-2 text-sm text-green-700">
                                        <p>You submitted this {hasTextAnswers ? 'answer set' : 'file'} on {formatDate(submission.submitted_at)}.</p>
                                        {(isMiddle || isPreparatory) && (
                                            <p className="mt-1">
                                                Remember to complete the self-assessment and peer assessment components.
                                            </p>
                                        )}
                                    </div>

                                    {/* Action buttons */}
                                    <div className="mt-4 flex items-center space-x-4">
                                        {hasFileSubmission && (
                                            <a
                                                href={`${FILE_BASE_URL}${submission.file_path}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-50 focus:ring-green-600"
                                            >
                                                <FileSymlink size={16} className="mr-1" />
                                                View Submission
                                            </a>
                                        )}

                                        {allowResubmit && (
                                            <button
                                                onClick={startResubmission}
                                                className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-blue-800 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-50 focus:ring-blue-600 border border-blue-200"
                                            >
                                                <Edit size={16} className="mr-1" />
                                                Re-submit
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Show submitted answers in read-only mode for text-based */}
                        {hasTextAnswers && (
                            <div className="space-y-4">
                                <h4 className="text-md font-medium text-gray-900">Your Submitted Answers:</h4>
                                {hasAssessmentQuestions ? (
                                    assignment.assessment_questions.map((question, index) => {
                                        const answer = submission.answers[index];
                                        return (
                                            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                <p className="text-sm font-medium text-gray-900 mb-2">
                                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-200 text-gray-600 rounded-full text-xs font-semibold mr-2">
                                                        {index + 1}
                                                    </span>
                                                    {question}
                                                </p>
                                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                    {answer || 'No answer provided'}
                                                </p>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                        <p className="text-sm font-medium text-gray-900 mb-2">Your Answer:</p>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                            {submission.answers?.join('\n') || 'No answer provided'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Case 3: Resubmission mode - Show edit form based on submission type */}
                {isResubmitting && (
                    <div className="space-y-4">
                        {/* Resubmission notice */}
                        <div className="rounded-md bg-blue-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <Edit className="h-5 w-5 text-blue-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">
                                        Re-submitting {submissionType === 'text' ? 'Answers' : 'File'}
                                    </h3>
                                    <p className="mt-1 text-sm text-blue-700">
                                        {submissionType === 'text'
                                            ? 'You can modify your answers below. This will replace your previous submission.'
                                            : 'Upload a new file to replace your previous submission.'
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Resubmission form based on type */}
                        {submissionType === 'text' ? (
                            <form onSubmit={handleTextSubmission} className="space-y-6">
                                <div className="space-y-4">
                                    {assignment.assessment_questions.map((question, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-900 mb-2">
                                                <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-100 text-orange-600 rounded-full text-xs font-semibold mr-2">
                                                    {index + 1}
                                                </span>
                                                {question}
                                            </label>
                                            <textarea
                                                value={answers[index] || ''}
                                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-vertical min-h-[100px]"
                                                placeholder="Type your answer here..."
                                                required
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={cancelResubmission}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <MessageSquare size={16} className="mr-2" />
                                                Update Answers
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleFileSubmission} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Upload new file</label>
                                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                                        <div className="space-y-1 text-center">
                                            <Upload className="mx-auto h-12 w-12 text-gray-400" />
                                            <div className="flex text-sm text-gray-600">
                                                <label
                                                    htmlFor="file-upload-resubmit"
                                                    className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none"
                                                >
                                                    <span>Upload a file</span>
                                                    <input
                                                        id="file-upload-resubmit"
                                                        name="file-upload-resubmit"
                                                        type="file"
                                                        className="sr-only"
                                                        onChange={handleFileChange}
                                                    />
                                                </label>
                                                <p className="pl-1">or drag and drop</p>
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                PDF, DOCX, XLSX, ZIP, PNG, JPG up to 10MB
                                            </p>
                                        </div>
                                    </div>
                                    {uploadFile && (
                                        <p className="mt-2 text-sm text-gray-600">
                                            Selected file: <span className="font-medium">{uploadFile.name}</span>
                                        </p>
                                    )}
                                </div>

                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={cancelResubmission}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={16} className="mr-2" />
                                                Update Submission
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubmissionForm;